import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, ArrowUpRight, Copy, Info, Loader2, Search, Sparkles } from 'lucide-react';

import { colorEngine } from '../lib/colorEngine';
import type { SemanticColorSuggestion } from '../lib/semanticColorSearch';
import { useToolState } from '../contexts/ToolStateContext';
import { useColorActions } from '../hooks/useColorActions';

interface SemanticColorSearchPanelProps {
  backgroundHex?: string | null;
  copiedField: string | null;
  onCopy: (value: string, field: string) => void;
  /** Pre-fill the search query (e.g. from URL deep-link ?q=) */
  initialQuery?: string;
}

interface ComplianceBadgeOptions {
  level: SemanticColorSuggestion['compliance'];
  meetsTarget: boolean;
}

const PRESET_QUERIES: Array<{ label: string; query: string }> = [
  { label: 'Calm teal on dark UI', query: 'calm teal for dark background AA compliant' },
  { label: 'Vibrant accent for light mode', query: 'vibrant magenta for light background AA compliant' },
  { label: 'AAA compliant headline', query: 'bright white for dark background AAA compliant' },
  { label: 'Neutral button text', query: 'neutral grey for brand blue background AA compliant' },
];

const DEBOUNCE_MS = 250;

function ComplianceBadge({ level, meetsTarget }: ComplianceBadgeOptions) {
  const palette = useMemo(() => {
    if (!meetsTarget) {
      return {
        className:
          'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300 border border-red-200 dark:border-red-800',
        label: level,
      };
    }
    switch (level) {
      case 'AAA':
        return {
          className:
            'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200 border border-emerald-200 dark:border-emerald-800',
          label: 'AAA',
        };
      case 'AA':
        return {
          className:
            'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-200 border border-blue-200 dark:border-blue-800',
          label: 'AA',
        };
      default:
        return {
          className:
            'bg-gray-100 text-gray-700 dark:bg-gray-900/50 dark:text-gray-200 border border-gray-200 dark:border-gray-700',
          label: level,
        };
    }
  }, [level, meetsTarget]);

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-semibold rounded-full ${palette.className}`}>
      <Sparkles className="w-3 h-3" />
      {palette.label}
    </span>
  );
}

function ResultCard({
  suggestion,
  copiedField,
  onCopy,
  onSetActive,
  onTagClick,
}: {
  suggestion: SemanticColorSuggestion;
  copiedField: string | null;
  onCopy: (value: string, field: string) => void;
  onSetActive?: (hex: string) => void;
  onTagClick?: (tag: string) => void;
}) {
  const fieldId = `semantic-${suggestion.id}`;
  return (
    <article className="sem-card">
      <div className="sem-card__header group">
        <div className="flex items-center gap-3">
          <div
            className="sem-card__swatch"
            style={{ backgroundColor: suggestion.hex }}
          />
          <div>
            <p className="sem-card__name">{suggestion.name}</p>
            <p className="sem-card__source">{suggestion.source.toUpperCase()}</p>
          </div>
        </div>
        <div className="sem-card__header-actions flex-shrink-0">
          <ComplianceBadge level={suggestion.compliance} meetsTarget={suggestion.meetsTarget} />
          <button
            type="button"
            className="sem-card__use-btn"
            onClick={() => onSetActive?.(suggestion.hex)}
            title="Use as active color"
            aria-label={`Use ${suggestion.hex} as active color`}
          >
            <ArrowUpRight size={12} />
          </button>
        </div>
      </div>

      <div className="sem-card__body">
        <div className="sem-card__hex-row">
          <button
            type="button"
            className="sem-card__hex-value"
            onClick={() => onCopy(suggestion.hex, fieldId)}
          >
            {copiedField === fieldId ? 'Copied ✓' : suggestion.hex}
          </button>
          <button
            type="button"
            onClick={() => onCopy(suggestion.hex, fieldId)}
            className="sem-card__copy-btn"
          >
            <Copy className="w-3 h-3" />
            {copiedField === fieldId ? 'Copied ✓' : 'Copy HEX'}
          </button>
        </div>
        <dl className="grid grid-cols-2 gap-2">
          <div>
            <dt className="sem-card__metric-label">Contrast</dt>
            <dd>
              {suggestion.contrastRatio != null ? (
                <button
                  type="button"
                  className="sem-card__metric-value"
                  onClick={() => onCopy(`${suggestion.contrastRatio!.toFixed(2)}:1`, `${fieldId}-contrast`)}
                >
                  {copiedField === `${fieldId}-contrast` ? 'Copied ✓' : `${suggestion.contrastRatio.toFixed(2)}:1`}
                </button>
              ) : (
                <span className="sem-card__metric-value sem-card__metric-value--empty">—</span>
              )}
            </dd>
          </div>
          <div>
            <dt className="sem-card__metric-label">ΔE to target</dt>
            <dd>
              {suggestion.deltaE != null ? (
                <button
                  type="button"
                  className="sem-card__metric-value"
                  onClick={() => onCopy(String(suggestion.deltaE), `${fieldId}-deltaE`)}
                >
                  {copiedField === `${fieldId}-deltaE` ? 'Copied ✓' : suggestion.deltaE}
                </button>
              ) : (
                <span className="sem-card__metric-value sem-card__metric-value--empty">—</span>
              )}
            </dd>
          </div>
        </dl>
        {suggestion.tags.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {suggestion.tags.map((tag) => (
              <button
                type="button"
                key={tag}
                className="sem-card__tag"
                onClick={() => onTagClick?.(tag)}
                title={`Search for "${tag}"`}
              >
                #{tag}
              </button>
            ))}
          </div>
        )}
        {suggestion.notes.length > 0 && (
          <ul className="space-y-1">
            {suggestion.notes.map((note, index) => (
              <li key={`${suggestion.id}-note-${index}`} className="sem-notes-item">
                <AlertTriangle className="w-3 h-3 mt-0.5" />
                <span>{note}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
      <footer className="sem-card__footer">
        Background reference:{' '}
        <button
          type="button"
          className="sem-card__footer-hex"
          onClick={() => onCopy(suggestion.background, `${fieldId}-bg`)}
        >
          {copiedField === `${fieldId}-bg` ? 'Copied ✓' : suggestion.background}
        </button>
      </footer>
    </article>
  );
}

export function SemanticColorSearchPanel({
  backgroundHex,
  copiedField,
  onCopy,
  initialQuery: initialQueryProp,
}: SemanticColorSearchPanelProps) {
  const { getToolState, setToolState } = useToolState();
  const persisted = getToolState('semantic');
  const [query, setQuery] = useState(initialQueryProp ?? persisted?.query ?? '');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<SemanticColorSuggestion[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Persist query on change
  useEffect(() => {
    setToolState('semantic', { query });
  }, [query, setToolState]);

  const { setActiveColor } = useColorActions();
  const handleSetActive = useCallback((hex: string) => {
    setActiveColor({ hex });
  }, [setActiveColor]);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setDebouncedQuery(query);
    }, DEBOUNCE_MS);
    return () => {
      window.clearTimeout(handle);
    };
  }, [query]);

  useEffect(() => {
    if (!debouncedQuery.trim()) {
      setSuggestions([]);
      setError(null);
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    colorEngine.semanticSearch(debouncedQuery, {
      fallbackBackground: backgroundHex ?? null,
      limit: 9,
    }).then((results) => {
      if (cancelled) return;
      setSuggestions(results);
      setError(results.length ? null : 'No suggestions matched this prompt. Try adjusting the description.');
    }).catch((searchError) => {
      if (cancelled) return;
      console.error('Semantic color search failed:', searchError);
      setSuggestions([]);
      setError('Unable to process this prompt.');
    }).finally(() => {
      if (!cancelled) setIsLoading(false);
    });

    return () => { cancelled = true; };
  }, [debouncedQuery, backgroundHex]);

  const contextNotice = useMemo(() => {
    if (!backgroundHex) {
      return null;
    }
    return `Using ${backgroundHex} as the fallback background when the prompt does not include one.`;
  }, [backgroundHex]);

  return (
    <section className="sem-section">
      <header className="space-y-3">
        <div className="flex flex-col gap-1">
          <h3 className="sem-header-title">Semantic Color Search</h3>
          <p className="sem-header-subtitle">
            Describe the intent (“calm teal for dark background AA compliant”) and receive curated swatches that balance mood
            and accessibility.
          </p>
        </div>
        <div className="relative flex flex-col sm:flex-row gap-2">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--ink-3)]" />
            <input
              type="text"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="e.g. calm teal for dark background AA compliant"
              className="sem-search__input"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {PRESET_QUERIES.map((preset) => (
              <button
                key={preset.label}
                type="button"
                onClick={() => setQuery(preset.query)}
                className="sem-preset"
              >
                <Sparkles className="w-3 h-3" />
                {preset.label}
              </button>
            ))}
          </div>
        </div>
        <div className="sem-info-notice">
          <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>
            Include mood modifiers (calm, vibrant, muted), background context ("for dark background"), and contrast goals ("AA
            compliant").
          </span>
        </div>
        {contextNotice && (
          <div className="sem-info-notice">
            <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>{contextNotice}</span>
          </div>
        )}
      </header>

      {isLoading && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={`skeleton-${i}`} className="sem-skeleton animate-pulse">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-12 h-12 rounded-lg sem-skeleton__bar" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 sem-skeleton__bar rounded w-3/4" />
                  <div className="h-3 sem-skeleton__bar rounded w-1/2" />
                </div>
              </div>
              <div className="space-y-2">
                <div className="h-3 sem-skeleton__bar rounded" />
                <div className="h-3 sem-skeleton__bar rounded w-5/6" />
              </div>
            </div>
          ))}
        </div>
      )}

      {!isLoading && !debouncedQuery.trim() && (
        <div className="sem-empty-state">
          Start typing a description above or pick one of the prompt presets to explore semantic matches.
        </div>
      )}

      {!isLoading && error && debouncedQuery.trim() && (
        <div className="sem-error-state">
          {error}
        </div>
      )}

      {!isLoading && !error && suggestions.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {suggestions.map((suggestion) => (
            <ResultCard
              key={suggestion.id}
              suggestion={suggestion}
              copiedField={copiedField}
              onCopy={onCopy}
              onSetActive={handleSetActive}
              onTagClick={(tag) => setQuery((prev) => prev ? `${prev} ${tag}` : tag)}
            />
          ))}
        </div>
      )}
    </section>
  );
}
